import React, { useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, TooltipProps } from 'recharts';
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle
} from '@/components/ui/card';
import {
	ChartConfig,
	ChartContainer,
	ChartTooltip,
} from '@/components/ui/chart';
import { cn } from '@/lib/utils'
import { CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

const chartConfig = {
	desktop: {
		label: "Desktop",
		// color: "hsl(var(--chart-1))",
		color: '#C0E6FF'
	},
} satisfies ChartConfig

interface StatsAreaChartProps {
	title: string
	data: Array<any>;
	xDataKey: string;
	yDataKey: string;

	description?: string
	icon?: React.ReactNode
	className?: string
	descriptionStyleOverride?: string
	isLoading?: boolean
}

interface CustomTooltipProps extends TooltipProps<number, string> { }

const CustomTooltip: React.FC<CustomTooltipProps> = ({
	active,
	payload,
	label,
}) => {
	if (!active || !payload || payload.length === 0) {
		return null;
	}

	// Assuming the `label` is a date string.
	const formattedDate = new Date(label as string)
		.toISOString()
		.substring(0, 10);
	// Use the first payload item (for 'ACTIVE_ACCOUNTS')
	const dataKey = payload[0].name;
	const value = payload[0].value as number;

	return (
		<div className="p-2 bg-[#011829]/80 backdrop-blur-md text-white rounded shadow">
			<p className="mb-1 text-sm font-medium">{formattedDate}</p>
			<p className="text-sm">
				{dataKey?.replace(/_/g, ' ')}: {value.toLocaleString()}
			</p>
		</div>
	);
};

export const StatsChart = (props: StatsAreaChartProps) => {
	const { title, data, icon, xDataKey, yDataKey, className, description, descriptionStyleOverride, isLoading = false } = props;

	const filteredData = useMemo(() => {
		if (!data) return [];
		const _data = data.slice();
		_data.pop();
		return _data.reverse();
	}, [data]);


	return (
		<Card className={cn(
			'bg-[#0C0F1D]/70 backdrop-blur-md text-black border-2 border-black rounded-sm shadow-[4px_4px_0_0_black] p-4',
			'transition-colors duration-150',
			className
		)}>
			<CardHeader className="p-0 mb-2">
				<div className='flex flex-row items-center justify-between'>
					<div className="flex items-center gap-2">
						{icon && (
							<div className="p-1 text-white">
								{icon}
							</div>
						)}
						<CardTitle className="text-sm font-bold text-white">{title}</CardTitle>
					</div>
				</div>
				{description && (
					<CardDescription className={cn('text-xs mt-1 text-white', descriptionStyleOverride)}>
						{description}
					</CardDescription>
				)}
			</CardHeader>

			<CardContent className="p-0 pt-2">
				{isLoading ?
					<Skeleton className='h-45 w-auto bg-white/20' />
					:
					!!filteredData?.length && (
						<ChartContainer config={chartConfig}>
							<AreaChart
								data={filteredData}
								margin={{ top: 12, bottom: 0 }}
							>
								<defs>
									<linearGradient id="gradient2" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor="#A084DC" stopOpacity={1.0} />
										<stop offset="95%" stopColor="#A084DC" stopOpacity={0.1} />
									</linearGradient>
								</defs>
								<CartesianGrid stroke="#000000" strokeDasharray="3 3" vertical={false} />
								<XAxis
									dataKey={xDataKey}
									tickLine={false}
									axisLine={false}
									tickMargin={8}
									// tick={{ fill: '#000000', fontSize: 10 }}
									tick={false}
									padding={{ left: 20, right: 20 }}
									tickFormatter={(value) =>
										new Date(value).toLocaleDateString('en-US', {
											timeZone: 'UTC',
											month: 'short',
											day: '2-digit'
										})
									}
								/>
								<ChartTooltip
									cursor={{ stroke: '#000000', strokeWidth: 2, strokeDasharray: '3 3' }}
									content={<CustomTooltip />}
								/>
								<Area
									dataKey={yDataKey}
									type="natural"
									fill="url(#gradient2)"
									stroke="#000000"
									strokeWidth={1.5}
								/>
							</AreaChart>
						</ChartContainer>
					)
				}
			</CardContent>
		</Card>
	)
}
